import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";

const testimonials = [
  {
    name: "张先生",
    role: "独立主播",
    content: "糖果直播是我每天都会看的资讯平台。通过他们的栏目推荐，我发现了很多优质的直播内容。新手教程也非常实用，帮助我在直播初期少走了很多弯路。",
    rating: 5,
  },
  {
    name: "李女士",
    role: "直播运营经理",
    content: "作为直播行业的从业者，糖果直播的行业趋势分析给了我很多启发。他们的内容不仅及时，而且深度和质量都很高，是行业内少有的优质内容平台。",
    rating: 5,
  },
  {
    name: "王同学",
    role: "直播爱好者",
    content: "我通过糖果直播的使用教程学会了直播的基础技巧，现在已经是一名兼职主播了。非常感谢平台提供这么全面实用的内容，推荐给所有想学直播的朋友。",
    rating: 5,
  },
  {
    name: "赵先生",
    role: "MCN机构创始人",
    content: "糖果直播的内容质量一直很稳定，尤其是平台对比评测和行业趋势类文章，对我们的业务决策有很高的参考价值。希望平台越做越好。",
    rating: 5,
  },
];

const stats = [
  { label: "优质文章", value: "500+" },
  { label: "覆盖分类", value: "4大" },
  { label: "服务用户", value: "10万+" },
  { label: "更新频率", value: "每日" },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setFormMessage("感谢您的留言，我们会尽快回复！");
      form.reset();
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9iclqec26n1c7c5psa0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && (parsed as { code?: string }).code === "OK") {
        setFormStatus("success");
        setFormMessage("感谢您的留言，我们会尽快回复！");
        form.reset();
      } else {
        const serverMsg =
          ((parsed as { meta?: { message?: string } }).meta?.message) ||
          ((parsed as { message?: string }).message) ||
          ((parsed as { meta?: { detail?: string } }).meta?.detail) ||
          responseText ||
          "提交失败，请稍后再试";
        setFormStatus("error");
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络错误，请检查网络连接后重试");
    }
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于糖果直播",
    description: "糖果直播致力于为用户提供最新、最全的直播资讯、栏目推荐和使用教程。了解我们的品牌故事、团队理念和联系方式。",
    url: "https://tangguolive.com/about",
    isPartOf: {
      "@type": "WebSite",
      name: "糖果直播",
      url: "https://tangguolive.com",
    },
  };

  return (
    <>
      <title>关于我们 - 糖果直播 | 品牌介绍与联系方式</title>
      <meta name="description" content="了解糖果直播的品牌故事、平台口碑和联系方式。我们致力于为用户提供最新、最全的直播资讯、栏目推荐和使用教程。期待与您的交流与合作。" />
      <meta name="keywords" content="糖果直播,关于我们,品牌介绍,联系方式,直播平台,口碑评价" />
      <link rel="canonical" href="https://tangguolive.com/about" />

      <meta property="og:type" content="website" />
      <meta property="og:title" content="关于我们 - 糖果直播" />
      <meta property="og:description" content="了解糖果直播的品牌故事、平台口碑和联系方式。" />
      <meta property="og:url" content="https://tangguolive.com/about" />

      <script type="application/ld+json">
        {JSON.stringify(aboutJsonLd)}
      </script>

      <div className="pt-20 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="mb-16 md:mb-20">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                关于糖果直播
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                汇聚直播资讯，连接精彩内容
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20warm%20atmosphere%2C%20team%20collaboration%20area%20with%20colorful%20decor%2C%20digital%20screens%20displaying%20content%2C%20bright%20natural%20light%2C%20contemporary%20workspace%20design%2C%20editorial%20photography%2C%20friendly%20and%20inviting%20environment&width=800&height=600&seq=about-brand-candy&orientation=landscape"
                  alt="糖果直播品牌形象"
                  title="糖果直播-品牌介绍"
                  className="w-full rounded-xl object-cover"
                />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  糖果直播成立于2024年，是一家专注于直播领域内容聚合与传播的互联网平台。我们致力于为用户提供最新、最全、最有价值的直播资讯、栏目推荐、使用教程和平台动态。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  在直播行业快速发展的今天，海量的信息让用户难以高效获取有价值的内容。糖果直播应运而生——我们以专业的内容团队和严谨的编辑标准，从海量信息中筛选出真正值得关注的内容，帮助用户节省时间、高效获取信息。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的团队由资深媒体人、直播行业从业者和技术专家组成，对直播行业有着深刻的理解和热爱。我们坚信，优质的内容能创造真正的价值。
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-14">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100"
                >
                  <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="mb-16 md:mb-20 py-14 md:py-20 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 rounded-none">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">
                来自用户真实的评价与反馈
              </p>
            </div>
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((item) => (
                <div
                  key={item.name}
                  className="p-6 rounded-xl bg-background-50"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{item.content}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      {item.name}
                    </p>
                    <p className="text-xs text-foreground-400">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                有任何问题或建议？欢迎随时与我们联系
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 flex flex-col gap-5">
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@tangguolive.com</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 mb-3">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-500">糖果直播资讯</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 mb-3">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <div className="lg:col-span-2">
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="p-6 md:p-8 rounded-xl bg-background-100"
                >
                  <h3 className="text-base font-semibold text-foreground-950 mb-6">
                    发送留言
                  </h3>

                  <div className="contact-form-honeypot">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>

                  {formStatus === "success" && (
                    <p className="mt-4 text-sm text-accent-600">{formMessage}</p>
                  )}
                  {formStatus === "error" && (
                    <p className="mt-4 text-sm text-red-500">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mt-16 md:mt-20 text-center py-12 rounded-2xl bg-background-100">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              加入糖果直播社区
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
              关注我们，第一时间获取最新的直播资讯和栏目推荐
            </p>
            <Link
              to="/"
              className="inline-flex px-8 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              返回首页浏览内容
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}